import { useState } from "react";
import { Smartphone, Monitor, Tablet, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDevices, UserDevice } from "@/hooks/useDevices";

export function DeviceManagement() {
  const { devices, isLoading, maxDevices, removeDevice } = useDevices();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = async (deviceId: string) => {
    setRemovingId(deviceId);
    await removeDevice(deviceId);
    setRemovingId(null);
  };

  const getDeviceIcon = (device: UserDevice) => {
    const os = device.os?.toLowerCase() || "";
    if (os.includes("ios") || os.includes("android")) {
      if (os.includes("ipad") || device.device_name.includes("平板")) {
        return <Tablet className="h-8 w-8 text-muted-foreground" />;
      }
      return <Smartphone className="h-8 w-8 text-muted-foreground" />;
    }
    return <Monitor className="h-8 w-8 text-muted-foreground" />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">登录设备管理</CardTitle>
        <CardDescription>
          已登录 {devices.length}/{maxDevices} 台设备
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {devices.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            暂无登录设备记录
          </p>
        ) : (
          devices.map((device) => (
            <div
              key={device.id}
              className="flex items-center gap-4 p-3 rounded-lg border bg-card"
            >
              {getDeviceIcon(device)}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{device.device_name}</p>
                <p className="text-xs text-muted-foreground">
                  IP: {device.ip_address || "未知"}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    disabled={removingId === device.id}
                  >
                    {removingId === device.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>确认删除设备？</AlertDialogTitle>
                    <AlertDialogDescription>
                      删除后，该设备将需要重新登录才能访问您的账号。
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>取消</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleRemove(device.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      删除
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))
        )}
        
        {devices.length >= maxDevices && (
          <p className="text-xs text-amber-600 text-center">
            已达设备数量上限，如需在新设备登录请先删除其他设备
          </p>
        )}
      </CardContent>
    </Card>
  );
}
